import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  IconButton,
  Fade,
} from '@mui/material';
import {
  Security as SecurityIcon,
  ContactMail as ContactIcon,
  ArrowUpward as ArrowUpIcon,
} from '@mui/icons-material';
import { environment } from '../../../environments/environment';
import SectionHeader from '../../components/common/SectionHeader';
// Structured legal content for Privacy Policy only
const legalSections = [
  {
    id: 'privacy',
    title: 'Privacy Policy',
    icon: <SecurityIcon />,
    description: 'How we collect, use, and protect your personal information',
    lastUpdated: '09/24/2025',
    content: [
      {
        subtitle: 'Information We Collect',
        items: [
          'Personal Information: Name, email, phone number, billing/shipping address, and payment details.',
          'Order Information: Items purchased, delivery details, and order history.',
          'Non-Personal Information: Browser type, device data, IP address, and site usage statistics via cookies or analytics tools.',
        ],
      },
      {
        subtitle: 'How We Use Your Information',
        text: 'We use collected information to:',
        items: [
          'Process and deliver orders.',
          'Communicate with you about orders, promotions, or updates.',
          'Improve our website, menu, and services.',
          'Comply with legal obligations.',
        ],
      },
      {
        subtitle: 'Sharing of Information',
        text: 'We do not sell or rent your personal information. We may share it:',
        items: [
          'With trusted service providers (payment processors, delivery partners).',
          'To comply with legal obligations.',
          'Between affiliated LLCs operating under the Fatty Patty Burger brand as needed to fulfill services.',
        ],
      },
      {
        subtitle: 'Cookies and Tracking',
        text: 'We use cookies and similar technologies to enhance your browsing experience and analyze site traffic. You may adjust cookie settings in your browser, but some features may not function properly without them.',
      },
      {
        subtitle: 'Data Security',
        text: 'We implement reasonable safeguards, but no method of transmission or storage is 100% secure.',
      },
      {
        subtitle: 'Your Rights',
        text: 'Depending on your location, you may have the right to:',
        items: [
          'Access, correct, or delete your personal information.',
          'Opt out of marketing emails.',
          'Restrict certain processing activities.',
        ],
      },
      {
        subtitle: "Children's Privacy",
        text: 'Our services are not directed to children under 13, and we do not knowingly collect information from them.',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact Us',
    icon: <ContactIcon />,
    description: 'How to reach us with questions or concerns',
    content: [
      {
        text: 'If you have questions about this Privacy Policy, please contact us:',
        contactInfo: {
          company: 'The Fatty Patty Burger – Asya LLC and Affiliated Entities',
          address: '4260 South 26th St. Milwaukee, WI. 53221',
          email: 'thefattypattymke@gmail.com',
          phone: '1-773-732-1170',
        },
      },
    ],
  },
];
const PrivacyPolicy = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {environment?.restaurantId !== 'fattyPattyDev' &&
        environment?.restaurantId !== 'fattyPatty' && (
          <Box
            sx={{
              width: '100%',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              pt: '82px',
            }}
          >
            <Container maxWidth="lg" sx={{ py: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: '#172B4D',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  mb: 2,
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  textAlign: 'center',
                  color: '#6C757D',
                  fontSize: '1.1rem',
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                Learn how we collect, use, and protect your personal information
              </Typography>
            </Container>
          </Box>
        )}
      {(environment?.restaurantId === 'fattyPattyDev' ||
        environment?.restaurantId === 'fattyPatty') && (
        <SectionHeader
          title="Privacy Policy"
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Privacy Policy', current: true },
          ]}
          fontFamily="Jost"
          showBreadcrumbs={true}
          titleVariant="h2"
          customStyles={{
            title: { fontSize: '40px', fontWeight: '700' },
            container: { minHeight: '250px' },
          }}
        />
      )}

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Chip
            label="Effective Date: September 24, 2025"
            sx={{
              mb: 2,
              backgroundColor: `${environment.mainColor}15`,
              color: environment.mainColor,
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          />
          <Typography
            variant="body1"
            sx={{
              color: '#6C757D',
              fontSize: '1.1rem',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            This Privacy Policy explains how the Fatty Patty Burger website and
            services, operated by Asya LLC and its affiliated entities, collect,
            use, and protect your personal information. By accessing or using
            our website or services, you agree to this policy.
          </Typography>
        </Box>

        {/* Legal Sections */}
        <Box sx={{ mb: 4 }}>
          {legalSections.map((section, index) => (
            <Fade in={true} timeout={300 + index * 100} key={section.id}>
              <Paper
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: '16px',
                  border: '1px solid #E5E7EB',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${environment.mainColor}08, ${environment.mainColor}15)`,
                    borderBottom: `1px solid ${environment.mainColor}20`,
                    py: 2,
                    px: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        backgroundColor: `${environment.mainColor}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        color: environment.mainColor,
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#172B4D',
                          fontSize: '1.25rem',
                          fontFamily: 'Jost, sans-serif',
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#6C757D',
                          fontSize: '0.95rem',
                          mt: 0.5,
                        }}
                      >
                        {section.description}
                      </Typography>
                    </Box>
                    {section.lastUpdated && (
                      <Chip
                        label={`Updated: ${section.lastUpdated}`}
                        size="small"
                        sx={{
                          ml: 2,
                          backgroundColor: '#E8F5E8',
                          color: '#2E7D32',
                          fontSize: '0.75rem',
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Content */}
                <Box sx={{ p: 4 }}>
                  {section.content.map((item, itemIndex) => (
                    <Box key={itemIndex} sx={{ mb: 3 }}>
                      {item.subtitle && (
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: '#172B4D',
                            fontSize: '1.1rem',
                            mb: 1.5,
                            fontFamily: 'Jost, sans-serif',
                          }}
                        >
                          {item.subtitle}
                        </Typography>
                      )}

                      {item.text && (
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#495057',
                            lineHeight: 1.7,
                            mb: item.items ? 1 : 0,
                            fontSize: '1rem',
                          }}
                        >
                          {item.text}
                        </Typography>
                      )}

                      {item.items && (
                        <Box sx={{ ml: 2 }}>
                          {item.items.map((listItem, listIndex) => (
                            <Box
                              key={listIndex}
                              sx={{ display: 'flex', mb: 1 }}
                            >
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  backgroundColor: environment.mainColor,
                                  mt: 1,
                                  mr: 2,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body1"
                                sx={{
                                  color: '#495057',
                                  lineHeight: 1.7,
                                  fontSize: '1rem',
                                }}
                              >
                                {listItem}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {item.contactInfo && (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            mt: 2,
                            borderRadius: '12px',
                            backgroundColor: '#F8F9FA',
                            border: '1px solid #E9ECEF',
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: '#172B4D',
                              mb: 2,
                              fontFamily: 'Jost, sans-serif',
                            }}
                          >
                            Contact Information
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600, color: '#172B4D' }}
                            >
                              {item.contactInfo.company}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ color: '#495057' }}
                            >
                              <span role="img" aria-label="Address">
                                📍
                              </span>{' '}
                              {item.contactInfo.address}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ color: '#495057' }}
                            >
                              <span role="img" aria-label="Email">
                                ✉️
                              </span>{' '}
                              {item.contactInfo.email}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ color: '#495057' }}
                            >
                              <span role="img" aria-label="Phone">
                                📞
                              </span>{' '}
                              {item.contactInfo.phone}
                            </Typography>
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Fade>
          ))}
        </Box>

        {/* Footer Note */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: '16px',
            backgroundColor: `${environment.mainColor}08`,
            border: `1px solid ${environment.mainColor}20`,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#495057',
              fontSize: '1rem',
              lineHeight: 1.7,
              fontStyle: 'italic',
            }}
          >
            This document was last updated on September 24, 2025. We recommend
            reviewing this page periodically to stay informed about how we
            protect your information and your rights as a user.
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
